import React, { useState, forwardRef } from "react";
import PropTypes from "prop-types";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import usePlanner from "features/planner/state/usePlanner";
import SlotContentEdit from "features/planner/components/slot/SlotContentEdit";
import colors from "app/theme/base/colors";
import { alpha } from "@mui/material/styles";

// forwardRef necesario para dnd-kit
const ExerciseSlotItem = forwardRef(
  ({ exercise, maxLines, fontSize, compact, onEdit, onDelete, dragListeners, isDragging }, ref) => {
    const [hovered, setHovered] = useState(false);
    const { isEditMode } = usePlanner();
    const {
      onKeyDown,
      onPointerDown,
      role,
      tabIndex,
      "aria-disabled": ariaDisabled,
      "aria-pressed": ariaPressed,
      "aria-roledescription": ariaRoleDescription,
      "aria-describedby": ariaDescribedBy,
    } = dragListeners || {};

    return (
      <div
        ref={ref}
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          padding: "4px 6px",
          borderRadius: "8px",
          backgroundColor: colors.background.paper,
          border: `1px solid ${colors.grey[300]}`,
          boxShadow: "none",
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          cursor: isEditMode ? "pointer" : hovered ? "pointer" : "grab",
          opacity: isDragging ? 0.35 : 1,
          transition: "opacity 0.15s ease",
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Contenido del slot */}
        {isEditMode ? (
          <SlotContentEdit slot={exercise} fontSize={fontSize} compact={compact} />
        ) : (
          <div
            style={{
              width: "100%",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",

              WebkitLineClamp: maxLines,
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              overflow: "hidden",

              lineHeight: "1.25rem",
              fontSize,
              color: colors.text.primary,
              fontWeight: 450,
              pointerEvents: hovered ? "none" : "auto",
            }}
          >
            {exercise.name}
          </div>
        )}

        {/* Overlay modo NORMAL */}
        {hovered && !isEditMode && (
          <div
            className="slot-overlay"
            style={{
              position: "absolute",
              inset: 0,
              background: alpha(colors.primary.main, 0.46),
              backdropFilter: "blur(3px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderRadius: "8px",
            }}
          >
            {/* EDITAR */}
            <div
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.();
              }}
              style={{
                flex: 1,
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRight: `1px solid ${alpha(colors.white.main, 0.24)}`,
                cursor: "pointer",
              }}
            >
              <EditIcon sx={{ fontSize: 18, color: "white.main" }} />
            </div>

            {/* DRAG HANDLE */}
            <div
              style={{
                flex: 1,
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRight: `1px solid ${alpha(colors.white.main, 0.24)}`,
                cursor: "grab",
                position: "relative",
              }}
            >
              <DragIndicatorIcon sx={{ fontSize: 20, color: "white.main" }} />

              <div
                onKeyDown={onKeyDown}
                onPointerDown={onPointerDown}
                role={role}
                tabIndex={tabIndex}
                aria-disabled={ariaDisabled}
                aria-pressed={ariaPressed}
                aria-roledescription={ariaRoleDescription}
                aria-describedby={ariaDescribedBy}
                style={{
                  position: "absolute",
                  inset: 0,
                  cursor: "grab",
                  background: "transparent",
                }}
              />
            </div>

            {/* BORRAR */}
            <div
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(exercise);
              }}
              style={{
                flex: 1,
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <DeleteIcon sx={{ fontSize: 18, color: "error.main" }} />
            </div>
          </div>
        )}

        {/* Overlay modo EDICIÓN (hover limpio, sin iconos) */}
        {hovered && isEditMode && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: alpha(colors.primary.main, 0.1),
              borderRadius: "8px",
              pointerEvents: "none",
            }}
          />
        )}
      </div>
    );
  }
);

ExerciseSlotItem.displayName = "ExerciseSlotItem";

ExerciseSlotItem.propTypes = {
  exercise: PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  maxLines: PropTypes.number.isRequired,
  fontSize: PropTypes.string.isRequired,
  compact: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  dragListeners: PropTypes.object,
  isDragging: PropTypes.bool,
};

export default ExerciseSlotItem;
